import {
  compareWithNameTiebreaker,
  getNonCategoryTotals,
} from '@macmillan-learning/gradebook-shared';
import dayjs from '../../../utils/dayjs';
import enLocale from 'dayjs/locale/en';
import { compose, get, groupBy, isArray, omit, reverse } from 'lodash/fp';
import _get from 'lodash/get';
import _isEqual from 'lodash/isEqual';
import _round from 'lodash/round';
import { getGBUrl } from '../../../utils/config';
import { postFetch } from '../../../utils/fetch';
import { getUserTimezone } from '../../../utils/util';
import { COURSE_LEVEL_ACTIVITIES_UNIT_ID } from './constants';
import {
  ASSIGNMENT_STATUS,
  ISG_TREND_NOT_AVAILABLE,
} from '../../../utils/constants';

export const getSettingsValue = (name) => (settings) =>
  isArray(settings)
    ? settings.find((setting) => setting.key === name)?.value
    : [];

export const getRoundPercentTypeValue = compose(
  Number,
  getSettingsValue('roundPercentType'),
);

export const getGradingType = compose(Number, getSettingsValue('gradingType'));

export const hasTimeAccommodationException = (assignment) => {
  return _get(
    assignment,
    'studentDueDateException.exception_value.additional_time_accommodation',
    false,
  );
};

export const getDueDateException = (assignment) => {
  return _get(
    assignment,
    'studentDueDateException.exception_value.due_date',
    null,
  );
};

export const getStudentAssignmentExceptionDate = (assignment) => {
  return _get(
    assignment,
    'studentAssignmentException.exception_value.due_date',
    null,
  );
};

// The ISG breaks up the due date exception and time accommodation exception into two different columns
// But the main gradebook treats them both as due date exceptions
// So this checks if the due date exception is different from the assignment due date, and if so, treats
// it as a time accommodation exception and NOT a due date exception
export const shouldShowDueDateExceptionIcon = (assignment) => {
  const dueDateException = getDueDateException(assignment);
  const studentExceptionDate = getStudentAssignmentExceptionDate(assignment);

  const dueDate =
    dueDateException || studentExceptionDate || assignment.assignment_due_at;
  const isDueDateException =
    dueDateException && !_isEqual(dueDate, assignment.assignment_due_at);

  return !!isDueDateException;
};

export const hasOpenedAssignments = (assignments = []) => {
  if (!Array.isArray(assignments)) {
    return false;
  }
  return assignments.some((assignment) => {
    const timeOnTask =
      assignment.timeOnTaskInMins && assignment.timeOnTaskInMins > 0;
    const isStarted = assignment.hasLaunched;
    return timeOnTask || isStarted;
  });
};

const getHoursAndMinutes = (totalMinutes) => {
  let hours = 0;
  let minutes = totalMinutes;

  if (minutes > 60) {
    hours = Math.floor(totalMinutes / 60);
    minutes = totalMinutes % 60;
  }

  return {
    hours: hours,
    minutes: minutes,
  };
};

export const getFormattedTime = (totalMinutes) => {
  if (!totalMinutes || !Number.isFinite(totalMinutes)) {
    return '';
  }

  const { hours, minutes } = getHoursAndMinutes(totalMinutes);
  let formattedTime;

  if (hours) {
    formattedTime = ''.concat(hours, 'h').concat(Math.round(minutes), 'm');
  } else {
    formattedTime = ''.concat(Math.round(minutes), 'm');
  }

  return formattedTime;
};

export const roundToTwoDecimals = (num) => {
  if (!num || !Number(num)) {
    return '0.00';
  }
  return Number(num).toFixed(2);
};

// Points earned are always in the format of 100.00,
// but points possible only show 2 decimals if they exist
export const formatISGPoints = (pointsEarned, pointsPossible) => {
  return {
    studentPoints: roundToTwoDecimals(pointsEarned || 0),
    assignedPoints: _round(pointsPossible || 0, 2),
  };
};

export const groupAssignmentsByCategory = compose(
  omit('undefined'),
  groupBy('gradebook_category_name'),
);

/**
 * Group an array of assignments by week
 * @param {AssignmentItem[]} allAssignments The complete list of assignments
 * @param {AssignmentItem[]} filteredAssignmentIds IDs of filtered assignments
 * @param startDate The start date of the course
 * @param startWeekOn The day of the week to start on, with 0 == Sunday
 * @param settings The gradebook settings
 * @returns An array representing the assignments per week,
 * along with the date range and name representing the week # (eg. 'Week 1');
 * any work before the start date will be grouped under 'Prework'
 */
export const groupAssignmentsByWeek = (
  allAssignments,
  filteredAssignmentIds,
  startDate,
  startWeekOn,
  settings,
) => {
  const customLocale = {
    ...enLocale,
    weekStart: startWeekOn,
  };

  const originalCourseStartDate = dayjs(startDate);
  // If the startWeekOn day is not the same day as originalCourseStartDate day,
  // then adjust to the closest previous date to originalCourseStartDate
  // that matches the startWeekOn day
  // EXAMPLE:
  // originalCourseStartDate is 06/04/25 (a Wednesday)
  // startWeekOn is 0 (Sunday)
  // adjustedCourseStartDateByWeekStart will calculate to 06/01/25
  // (the closest Sunday before 06/04/25)
  const adjustedCourseStartDateByWeekStart = originalCourseStartDate
    .locale(customLocale)
    .startOf('week');

  const result = {};

  const getWeekNumber = (assignmentDueDate) =>
    1 +
    assignmentDueDate
      .locale(customLocale)
      .diff(adjustedCourseStartDateByWeekStart, 'week');

  const getAssignmentDateRange = (weekNumber) => {
    // Every date range is a full 7 days
    // Week 1 starts from adjustedCourseStartDateByWeekStart
    // AKA the closest previous date to originalCourseStartDate
    // that matches the startWeekOn day
    const weekStartDate = dayjs(adjustedCourseStartDateByWeekStart).add(
      weekNumber - 1,
      'week',
    );

    return {
      from: weekStartDate.toDate(),
      to: dayjs(weekStartDate).locale(customLocale).endOf('week').toDate(),
    };
  };

  const createAssignmentGroup = (
    weekNumber,
    name,
    dateRange = { from: null, to: null },
  ) => ({
    id: weekNumber,
    name,
    unfilteredAssignments: [], // all assignments
    assignments: [], // filtered assignments
    dateRange,
  });

  allAssignments.forEach((assignment) => {
    const assignmentDueDate = dayjs(assignment.studentAssignmentDueAt);

    if (assignmentDueDate.isBefore(adjustedCourseStartDateByWeekStart)) {
      if (!result[0]) {
        result[0] = createAssignmentGroup(0, 'Prework');
      }
      result[0].unfilteredAssignments.push(assignment);

      if (filteredAssignmentIds.includes(assignment.id)) {
        result[0].assignments.push(assignment);
      }

      return;
    }

    const weekNumber = getWeekNumber(assignmentDueDate);
    if (!result[weekNumber]) {
      result[weekNumber] = createAssignmentGroup(
        weekNumber,
        `Week ${Number(weekNumber)}`,
        getAssignmentDateRange(weekNumber),
      );
    }
    result[weekNumber].unfilteredAssignments.push(assignment);

    if (filteredAssignmentIds.includes(assignment.id)) {
      result[weekNumber].assignments.push(assignment);
    }
  });

  // Calculate the totals for each week on the filtered assignments only
  const weekNumbers = Object.keys(result);
  if (weekNumbers.length > 0) {
    for (let i = 0; i < weekNumbers.length; i++) {
      const weekNumber = weekNumbers[i];
      result[weekNumber].total = getNonCategoryTotals(
        result[weekNumber].assignments,
        settings,
        'Week Total',
      );
    }
  }

  return Object.values(result);
};

/**
 * Get the current week number
 * @param {AssignmentItem[]} assignmentsByWeek The complete list of assignments grouped by week using the groupAssignmentsByWeek function
 * @returns The current week number
 */
export const getCurrentWeek = (assignmentsByWeek) => {
  const currentWeek = assignmentsByWeek.find((week) => {
    const { from, to } = week.dateRange;
    const currentDate = new Date();
    // Check if currentDate is between from and to (inclusive)
    return currentDate >= from && currentDate <= to;
  });

  return currentWeek?.id;
};

/**
 * Group an array of assignments by unit (aka folder)
 * @param {AssignmentItem[]} allAssignments The complete list of assignments
 * @param {AssignmentItem[]} filteredAssignmentIds IDs of filtered assignments
 * @param folders The list of folders (ie units)
 * @param settings The gradebook settings
 * @returns An array representing the assignments per unit
 */
export const groupAssignmentsByUnit = (
  allAssignments,
  filteredAssignmentIds,
  folders,
  settings,
) => {
  const folderIdToRootFolderIdMap = getFolderIdToRootFolderIdMap(folders ?? []);
  const groups = {};

  const createAssignmentGroup = (folderId, name) => ({
    id: folderId,
    name,
    unfilteredAssignments: [], // all assignments
    assignments: [], // filtered assignments
  });

  // Course Level Activities group (for assignments that are not in any folder) come first
  groups[COURSE_LEVEL_ACTIVITIES_UNIT_ID] = createAssignmentGroup(
    COURSE_LEVEL_ACTIVITIES_UNIT_ID,
    'Course Level Activities',
  );

  // All root folders come next
  folders
    ?.filter((folder) => !folder.parent_id)
    .forEach((folder) => {
      groups[folder.id] = createAssignmentGroup(folder.id, folder.name);
    });

  // Add each assignment to its root folder's group
  allAssignments.forEach((assignment) => {
    let folderId;
    const rootFolderId = folderIdToRootFolderIdMap[assignment.folder_id];

    if (
      assignment.folder_id == null ||
      rootFolderId == null ||
      groups[rootFolderId] == null
    ) {
      folderId = COURSE_LEVEL_ACTIVITIES_UNIT_ID;
    } else {
      folderId = rootFolderId;
    }

    groups[folderId].unfilteredAssignments.push(assignment);

    if (filteredAssignmentIds.includes(assignment.id)) {
      groups[folderId].assignments.push(assignment);
    }
  });

  // Remove folders that have no assignments (ignoring filtering)
  for (const unitId in groups) {
    if (groups[unitId].unfilteredAssignments.length === 0) {
      delete groups[unitId];
    }
  }

  // Calculate the totals for each folder/unit on the filtered assignments only
  const units = Object.keys(groups);
  if (units.length > 0) {
    for (let i = 0; i < units.length; i++) {
      const unitId = units[i];
      groups[unitId].total = getNonCategoryTotals(
        groups[unitId].assignments,
        settings,
        'Unit Total',
      );
    }
  }

  return Object.values(groups);
};

// Create a map of every folder ID to its root parent folder ID
const getFolderIdToRootFolderIdMap = (courseFolders) => {
  const folderIdToRootFolderIdMap = {};

  courseFolders
    .filter((folder) => !folder.parent_id)
    .forEach((rootFolder) => {
      // Add itself
      folderIdToRootFolderIdMap[rootFolder.id] = rootFolder.id;

      // Add all children
      const childFolderIds = getAllChildFolderIds(courseFolders, rootFolder.id);
      childFolderIds.forEach((childFolderId) => {
        folderIdToRootFolderIdMap[childFolderId] = rootFolder.id;
      });
    });

  return folderIdToRootFolderIdMap;
};

// Get all child folder IDs for a given parent folder ID, including all children of children, etc.
const getAllChildFolderIds = (courseFolders, parentId) => {
  const childFolderIds = courseFolders
    .filter((folder) => folder.parent_id === parentId)
    .map((folder) => folder.id);

  if (childFolderIds.length === 0) return [];

  return [
    ...childFolderIds,
    ...childFolderIds.flatMap((childFolderId) =>
      getAllChildFolderIds(courseFolders, childFolderId),
    ),
  ];
};

const dueDateSorter = (a, b) => {
  const aDateDueDate = a?.assignment_due_at;
  const bDateDueDate = b?.assignment_due_at;

  const aExceptionDate = getDueDateException(a);
  const bExceptionDate = getDueDateException(b);

  const aAssignmentException = getStudentAssignmentExceptionDate(a);
  const bAssignmentException = getStudentAssignmentExceptionDate(b);

  const aDate = aExceptionDate || aAssignmentException || aDateDueDate;
  const bDate = bExceptionDate || bAssignmentException || bDateDueDate;

  const dateDiff = +new Date(aDate) - +new Date(bDate);
  return compareWithNameTiebreaker(dateDiff, a, b);
};

const dateCompletedSorter = (a, b) => {
  const aDate = new Date(a?.dateCompleted);
  const bDate = new Date(b?.dateCompleted);

  const dateDiff = +aDate - +bDate;
  return compareWithNameTiebreaker(dateDiff, a, b);
};

const timeOnTaskSorter = (a, b) => {
  const aTime = a?.timeOnTaskInMins;
  const bTime = b?.timeOnTaskInMins;

  if (!aTime) return -1;
  const timeDiff = Number(aTime) - Number(bTime);
  return compareWithNameTiebreaker(timeDiff, a, b);
};

const scoreSorter = (a, b) => {
  const aScore = a?.studentGrade;
  const bScore = b?.studentGrade;

  if (a.status === 'Missed') return -1;

  const scoreDiff = Number(aScore) - Number(bScore);
  return compareWithNameTiebreaker(scoreDiff, a, b);
};

const nameSorter = (a, b) => a.name.localeCompare(b.name);

export const assignmentsSorter = (sortBy, assignments = []) => {
  switch (sortBy) {
    case 'assignmentName':
      return assignments.sort(nameSorter);
    case 'score':
      return assignments.sort(scoreSorter);
    case 'dueDate':
      return assignments.sort(dueDateSorter);
    case 'completed':
      return assignments.sort(dateCompletedSorter);
    case 'timeSpent':
      return assignments.sort(timeOnTaskSorter);
    default:
      return assignments;
  }
};

export const sortAssignments = (sortBy, sortOrder, assignments) => {
  const sortedAssignments = assignmentsSorter(sortBy, assignments);
  return sortOrder === 'asc' ? sortedAssignments : reverse(sortedAssignments);
};

export const getStudentAssignmentFromCategories = (categories) => {
  return categories.flatMap(({ assignments }) => assignments);
};

export const getStudentFullName = (student) => {
  const { first_name: firstName, last_name: lastName } = student ?? {};

  return `${firstName} ${lastName}`;
};

// Selector helpers
export const selectIsgData = get(
  'gradebook.individualStudentGradebookData.studentGradebook',
);
export const selectStudent = get(
  'gradebook.individualStudentGradebookData.currentStudent',
);
export const selectShowPercent = get(
  'gradebook.individualStudentGradebookData.showPercent',
);
export const selectTimeOnTaskMap = get(
  'gradebook.individualStudentGradebookData.timeOnTaskMap',
);
export const selectIndividualStudentGradebookIsLoading = get(
  'gradebook.individualStudentGradebookIsLoading',
);

export const getFormattedLatestActivityTimestamp = (date) => {
  const dateParsed = date && dayjs(date);
  // Updated format to abbreviate month and include 'at' before the time
  const formattedString =
    dateParsed && dateParsed.isValid()
      ? dateParsed.format('MMM D, YYYY [at] h:mm A')
      : '';

  return formattedString;
};

// Function to convert fetched data to CSV format and initiate download
export const handleExportCSV = async (courseId, studentDetails) => {
  try {
    const userTimezone = getUserTimezone();
    const reqUrl = `${getGBUrl()}/api/v1/launches/courses/${courseId}/users/${studentDetails.studentId}/activity-logs/export`;
    const body = {
      userTimezone,
      sort: [{ by: 'launched_at', order: 'desc', sequence: 1 }],
    };

    const response = await postFetch(reqUrl, body);

    if (!response.ok) {
      throw new Error(`Server request failed with status ${response.status}`);
    }

    const blob = await response.blob();
    const filename =
      response.headers.get('Content-Disposition')?.split('filename=')[1] ??
      `ActivityLog_${dayjs().format('YYYYMMDD')}.csv`;

    // For IE 10+
    if (navigator.msSaveBlob) {
      navigator.msSaveBlob(blob, filename);
    } else {
      const link = document.createElement('a');
      if (link.download !== undefined) {
        const url = URL.createObjectURL(blob);
        link.setAttribute('href', url);
        link.setAttribute('download', filename);
        link.style.visibility = 'hidden';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }
    }
  } catch (error) {
    console.error('Error exporting CSV:', error);
  }
};

export const getScoreChangeFromPrevious = (currentTotal, previousTotal) => {
  if (!currentTotal || !previousTotal) {
    return null;
  }

  if (currentTotal.assignedPoints === 0 || previousTotal.assignedPoints === 0) {
    return ISG_TREND_NOT_AVAILABLE;
  }

  const currentPercent =
    currentTotal.studentPoints / currentTotal.assignedPoints;
  const previousPercent =
    previousTotal.studentPoints / previousTotal.assignedPoints;
  const difference = currentPercent - previousPercent;

  return roundToTwoDecimals(difference * 100);
};
export const isUncompletePastDueAssignment = (assignment) => {
  const isCompleted =
    assignment.status.toLowerCase() ===
    ASSIGNMENT_STATUS.COMPLETED.toLowerCase();
  if (isCompleted) return false;

  const dueDate = assignment.studentAssignmentDueAt;
  if (!dueDate) return false;

  // Due date must be in the past
  return new Date() > new Date(dueDate);
};
export const hasFirstDueDatePassed = (assignments, now = new Date()) => {
  if (!Array.isArray(assignments) || assignments.length === 0) return false;
  const validDates = [];
  for (const assignment of assignments) {
    const dueDate = assignment?.studentAssignmentDueAt;
    if (dueDate != null) {
      validDates.push(new Date(dueDate));
    }
  }

  return validDates.length > 0 && now > validDates[0];
};
